import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom'
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { BankChangeDashletContent } from "../../dashlets/BankChangeDashlet";
import { selectDropdownOptions } from "../../../redux/dropdownSlice";
import { selectUser } from "../../../redux/userSlice";
import { triggerRerender } from "../../../redux/rerenderSlice";
import Loading from '../../../components/Loading'

export default function BankAccountModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const user = useSelector(selectUser);
  const { handleSubmit, control, register, formState: { errors }, reset, setValue } = useForm();
  const [saving, setSaving] = useState(false)
  const [validateStep, setValidateStep] = useState('lookup')
  const [validateResult, setValidateResult] = useState(null)
  const [bankAccountData, setBankAccountData] = useState({})
  const [isRequiredManagerApproval, setIsRequiredManagerApproval] = useState(false)
  const request = useRequest()
  const customerId = modal.options?.customerId
  const dropdownOptions = useSelector(selectDropdownOptions);
  const mgrApprovalApproval = dropdownOptions.filter((o) => o.code_key === 'mgr_approval_status_required')[0]
  const bankAccountNumberRef = useRef(null);
  const [canBypassBankVerification, setCanBypassBankVerification] = useState(false);
  const inFlightCustomerId = useRef(null);

  const resetState = () => {
    inFlightCustomerId.current = null
    setValidateStep('lookup')
    setValidateResult(null)
    setIsRequiredManagerApproval(false)
    setCanBypassBankVerification(false)
    setSaving(false)
    setBankAccountData({})
    reset();
  }

  useEffect(() => {
    if (modal.type === 'bank_account_modal' && modal.is_active) {
      // This modal stays mounted in the Footer; start every opening from a clean slate so the
      // previous customer's lookup result and bypass flag can't carry over.
      resetState()
      if (modal.options?.bank_account_number && modal.options?.bank_routing_number) {
        setValidateStep('validate')
        const bankAccountNumber = modal.options.bank_account_number.replaceAll('#', '')
        if (!isNumeric(bankAccountNumber.trim())) {
          dispatch(setNotification({ type: 'error', body: "Account number can only contain digits." }));
          return false;
        }
        if (!isNumeric(modal.options.bank_routing_number.trim())) {
          dispatch(setNotification({ type: 'error', body: "Routing number can only contain digits." }));
          return false;
        }
        lookupBankAccount(bankAccountNumber, modal.options.bank_routing_number.trim())
      }

      if (validateStep === 'validate') {
        bankAccountNumberRef.current?.focus();
      }
    }
  }, [modal]);

  useEffect(() => {
    if (Object.keys(bankAccountData).length > 0 && modal.options?.bank_account_number) {
      setValue('bank_account_number', bankAccountData.bank_account_number)
      setValue('bank_routing_number', bankAccountData.bank_routing_number)
    }
  }, [bankAccountData])

  /**
   * Search API for bank account number that matches the passed in parameters
   * NOTE: Sometimes this is going to search on last 4 instead of full number is #s are included in bank account number
   * @param bankAccountNumber
   * @param bankRoutingNumber
   * @returns {Promise<void>}
   */
  const lookupBankAccount = async (bankAccountNumber, bankRoutingNumber) => {
    setSaving(true)
    let endpoint = `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/bank_account/search`;
    // This looks up a bank account number, so it uses POST instead of GET to keep the
    // value out of the URL. useRequest's post() doesn't append spoofUserID like get()
    // does, so it's added manually here: the backend reads it from the query string,
    // not the JSON body.
    if (user.spoofed_user?.id) {
      endpoint += `?spoofUserID=${user.spoofed_user.id}`
    }

    request.setEndpoint(endpoint);
    inFlightCustomerId.current = customerId
    const response = await request.post({ bank_account_number: bankAccountNumber, bank_routing_number: bankRoutingNumber });
    // Ignore a response that a close or a newer opening has superseded.
    if (inFlightCustomerId.current !== customerId) return
    if (response.status === 200 || response.status === 201) {
      setSaving(false)
      setValidateStep('validate')
      setBankAccountData(response?.data?.data)
      setCanBypassBankVerification(response?.data?.can_bypass_verification)
    } else {
      setSaving(false)
      setValidateStep('lookup')
      dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
    }
  }

  function isNumeric(str) {
    return /^\d+$/.test(str);
  }

  const onSubmit = async (formData) => {
    if (!isNumeric(formData.bank_account_number.trim())) {
      dispatch(setNotification({ type: 'error', body: "Account number can only contain digits." }));
      return false;
    }
    if (!isNumeric(formData.bank_routing_number.trim())) {
      dispatch(setNotification({ type: 'error', body: "Routing number can only contain digits." }));
      return false;
    }
    setSaving(true)

    let endpoint = `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/bank_account`;
    try {
      if (validateStep === 'lookup') {
        await lookupBankAccount(formData.bank_account_number, formData.bank_routing_number)
      } else if (validateStep === 'validate' || validateStep === 'confirm_duplicate') {
        request.setEndpoint(endpoint)
        formData.is_confirmed_duplicate = validateStep === 'confirm_duplicate';
        const response = await request.post(formData);
        if (response.status === 200 || response.status === 201) {
        setSaving(false)
        const result = response?.data
          setValidateResult(result)
          setValidateStep('result')
          if (result?.data?.model_processing_status_ddo?.code_key === 'processing_status_verify') {
            setIsRequiredManagerApproval(true)
            dispatch(triggerRerender({ subpanelId: 'customer_action_button' }))
          }
          handleBankChangeComplete()
          /** If there was a new bank change created, rerender the bank change dashlet */
          if (result?.data?.id) {
            dispatch(triggerRerender({ subpanelId: 'bank_change_dashlet' }));
          }
          /** Display an error message in any case if the bank change outcome is false */
          if (result?.data?.bank_change_outcome_ddo?.optional_value_2 === 'FALSE') {
            let errorMessage = result?.message ?? result?.data?.bank_change_outcome_ddo?.optional_value_1;
            if (!errorMessage) {
              errorMessage = 'Bank Change Unsuccessful';
            }
            dispatch(setNotification({ type: 'error', body: errorMessage }))
          }
        } else {
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      }
    } catch (error) {
      setSaving(false)
      let errorMessage = error?.response?.message ?? error?.response?.data?.message;
      if (!errorMessage) {
        errorMessage = error?.response?.data?.messageExtreme ?? 'Bank Account Update Failed';
      }
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      if ([403, 412, 422].includes(error?.response?.status)) {
        handleBankChangeComplete()
      }
    }
  }

  const handleBankChangeComplete = () => {
    dispatch(setModal({
      is_active: false
    }));
    dispatch(triggerRerender({ subpanelId: 'bank_change_dashlet' }));
    dispatch(triggerRerender({ subpanelId: 'manual_verification' }));
    resetState()
  };

  const checkNameNotBank = (inputVal) => {
    if (!inputVal) return true;
    const bankName = bankAccountData?.bank_name?.toLowerCase();
    return inputVal?.toLowerCase() !== bankName || "Please enter the account holder's name and not the bank";
  }

  return modal.is_active && modal.type === 'bank_account_modal' ? (
    <form onSubmit={handleSubmit(onSubmit)} id="bank_account_modal_form" className="lox-form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-3">
          {saving && (
            <div className="loader-wrapper">
              <Loading />
            </div>
          )}
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Update Bank Account Information
            </p>
            <button className="delete" aria-label="close" onClick={handleBankChangeComplete}></button>
          </header>
          {(validateStep === 'lookup' || validateStep === 'validate') && (
            <section
              className="modal-card-body modal-card-body pt-1 pb-1"
              style={{ minHeight: "100px", overflow: 'hidden' }}
            >
              <div className="columns mb-0 is-flex-direction-column mt-3">
                <div className="column">
                  <h3>{`From this form you can select from the customer's existing bank accounts OR create a new bank account.`}</h3>
                </div>
                <div className="column">
                  <div className="field">
                    <label htmlFor="bank_account_number">
                     Bank Account Number
                     <span className="has-text-danger">*</span>
                    </label>
                    <Controller
                      name="bank_account_number"
                      control={control}
                      rules={{
                        required: "Bank account number is required",
                        validate: {
                          specialCharacters: (bankAccountNumber) => isNumeric(bankAccountNumber) || "Account number can only contain digits."
                        }
                      }}
                      defaultValue={bankAccountData?.bank_account_number}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          id="bank_account_number"
                          className="input is-small"
                          ref={bankAccountNumberRef}
                        />
                      )}
                    />
                     <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                       {errors.bank_account_number && <span>{errors.bank_account_number.message ? errors.bank_account_number.message : "Bank Account Number is required"}</span>}
                     </p>
                  </div>
                </div>
                <div className="column">
                  <div className="field">
                    <label htmlFor="bank_routing_number">
                     Routing Number
                     <span className="has-text-danger">*</span>
                    </label>
                    <Controller
                      name="bank_routing_number"
                      control={control}
                      rules={{
                        required: "Bank routing number is required",
                        validate: {
                          specialCharacters: (routingNumber) => isNumeric(routingNumber) || "Routing number can only contain digits.",
                        }
                      }}
                      defaultValue={bankAccountData?.bank_routing_number}
                      render={({ field }) => (
                        <input {...field} type="text" id="bank_routing_number" className="input is-small" />
                      )}
                    />
                     <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                       {errors.bank_routing_number && <span>{errors.bank_routing_number.message ? errors.bank_routing_number.message : "Bank Routing Number is required"}</span>}
                     </p>
                  </div>
                </div>
                {(validateStep === 'validate') && (
                  <>
                    <div className="column">
                      <div className="field">
                        <label htmlFor="bank_account_holder_name">
                         Bank Account Holder Name
                         <span className="has-text-danger">*</span>
                        </label>
                        <Controller
                          name="bank_account_holder_name"
                          control={control}
                          rules={{
                            required: "Bank Account Holder Name is required",
                            validate: {
                              specialCharacters: (name) => /^(?!.*[0-9])/.test(name) || "You must enter an account holder name with no special characters",
                              nameNotBank: checkNameNotBank,
                            },
                          }}
                          defaultValue={bankAccountData?.bank_account_holder_name}
                          render={({ field }) => (
                            <input {...field} type="text" id="bank_account_holder_name" className="input is-small" />
                          )}
                        />
                        <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                         {errors.bank_account_holder_name && <span>{errors.bank_account_holder_name.message || errors.bank_account_holder_name.message }</span>}
                        </p>
                      </div>
                    </div>
                    {bankAccountData?.bank_name && (
                      <div className="column">
                        <div className="field">
                          <label htmlFor="bank_name">
                           Bank Name
                          </label>
                          <h3>{bankAccountData?.bank_name}</h3>
                        </div>
                      </div>
                    )}
                    <div className="column">
                      <div className="field">
                        <label htmlFor="is_bank_savings">
                         Account Type
                         <span className="has-text-danger">*</span>
                        </label>
                        <Controller
                          name="is_bank_savings"
                          control={control}
                          render={({ field }) => (
                            <div className="control">
                              <label className="radio">
                                <input defaultChecked={bankAccountData?.is_bank_savings === 0} className="mr-1" {...register("is_bank_savings", { required: true })} value="0" type="radio" name="is_bank_savings" />
                                Checking
                              </label>
                              <label className="radio ml-5">
                                <input defaultChecked={bankAccountData?.is_bank_savings === 1} className="mr-1" {...register("is_bank_savings", { required: true })} value="1" type="radio" name="is_bank_savings" />
                                Saving
                              </label>
                            </div>
                          )}
                        />
                        <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                         {errors.is_bank_savings && <span>{errors.is_bank_savings.message ? errors.is_bank_savings.message : "Account Type is required"}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="column">
                      <div className="field">
                        <label htmlFor="is_online_banking">
                         Has Online Banking
                         <span className="has-text-danger">*</span>
                        </label>
                        <Controller
                          name="is_online_banking"
                          control={control}
                          render={({ field }) => (
                            <div className="control">
                              <label className="radio">
                                <input defaultChecked={bankAccountData?.is_online_banking === 1} className="mr-1" {...register("is_online_banking", { required: true })} value="1" type="radio" name="is_online_banking" />
                                Yes
                              </label>
                              <label className="radio ml-5">
                                <input defaultChecked={bankAccountData?.is_online_banking === 0} className="mr-1" {...register("is_online_banking", { required: true })} value="0" type="radio" name="is_online_banking" />
                                No
                              </label>
                            </div>
                          )}
                        />
                        <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                         {errors.is_online_banking && <span>{errors.is_online_banking.message ? errors.is_online_banking.message : " Online Banking is required"}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="column">
                      <div className="field">
                        <label htmlFor="is_direct_deposit">
                         Is Direct Deposit
                         <span className="has-text-danger">*</span>
                        </label>
                        <Controller
                          name="is_direct_deposit"
                          control={control}
                          render={({ field }) => (
                            <div className="control">
                              <label className="radio">
                                <input defaultChecked={bankAccountData?.is_direct_deposit === 1} className="mr-1" {...register("is_direct_deposit", { required: true })} value="1" type="radio" name="is_direct_deposit" />
                                Yes
                              </label>
                              <label className="radio ml-5">
                                <input defaultChecked={bankAccountData?.is_direct_deposit === 0} className="mr-1" {...register("is_direct_deposit", { required: true })} value="0" type="radio" name="is_direct_deposit" />
                                No
                              </label>
                            </div>
                          )}
                        />
                        <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                         {errors.is_direct_deposit && <span>{errors.is_direct_deposit.message ? errors.is_direct_deposit.message : " Direct Deposit is required"}</span>}
                        </p>
                      </div>
                    </div>
                    { canBypassBankVerification && (
                    <div>
                      <Controller
                        name="bypass_bank_verification"
                        control={control}
                        rules={{ required: false }}
                        defaultValue={false}
                        render={({ field }) => (
                          <input {...field} type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                        )}
                      />
                      <label className="pl-1 formLabel">
                        Bypass Verification
                      </label>
                    </div>
                  )}
                  </>
                )}
              </div>
            </section>
          )}
          {validateStep === 'result' && (
            <section
              className="modal-card-body modal-card-body pt-1 pb-1"
              style={{ minHeight: "100px", overflow: 'hidden' }}
            >
              <div className="columns is-full m-2 p-0 is-align-items-center">
                <div className="column">
                  <BankChangeDashletContent dropdownOptions={dropdownOptions} row={validateResult} />
                </div>
              </div>
            </section>
          )}
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              {validateStep === 'lookup' && (
                <>
                  <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleBankChangeComplete}>Cancel</button>
                  <button type="submit" className="button is-link is-small" disabled={saving && 'disabled'}>{saving ? 'Looking Up' : 'Look Up'}</button>
                </>
              )}
              {validateStep === 'validate' && (
                <>
                  <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleBankChangeComplete}>Cancel</button>
                  <button type="submit" className="button is-link is-small" disabled={saving && 'disabled'}>{saving ? 'Validating' : 'Validate'}</button>
                </>
              )}
              {validateStep === 'result' && (
                <button type="button" className="button is-link is-small" onClick={handleBankChangeComplete}>Done</button>
              )}
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
